import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Save, Link as LinkIcon, Copy, Camera, Upload, Loader2, Trash2, Image as ImageIcon, Building2, Type, RefreshCw, Check, Eye, ChevronDown, ChevronUp, Droplet, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../../App";

// Watermark position options
const WATERMARK_POSITIONS = [
  { value: "top-left", label: "Top Left" },
  { value: "top-center", label: "Top Center" },
  { value: "top-right", label: "Top Right" },
  { value: "center-left", label: "Center Left" },
  { value: "center", label: "Center" },
  { value: "center-right", label: "Center Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-center", label: "Bottom Center" },
  { value: "bottom-right", label: "Bottom Right" }
];

// Watermark Settings Component (moved from WatermarkPage)
const WatermarkSettings = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    type: "text",
    text: "",
    font_size: 36,
    opacity: 50,
    position: "bottom-right",
    color: "#FFFFFF",
    apply_to_downloads: true,
    apply_to_previews: true,
    watermark_image: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/watermark/settings`, { withCredentials: true });
      setSettings(prev => ({ ...prev, ...response.data }));
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/watermark/settings`, settings, { withCredentials: true });
      toast.success("Watermark settings saved");
      loadPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/watermark/preview`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
    } catch (error) {
      console.log("Preview not available");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/watermark/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, type: "image", watermark_image: response.data.filename }));
      toast.success("Watermark image uploaded");
      loadPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const deleteWatermarkImage = async () => {
    try {
      await axios.delete(`${API}/watermark/image`, { withCredentials: true });
      setSettings(prev => ({ ...prev, type: "text", watermark_image: null }));
      toast.success("Watermark image deleted");
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!hasAccess) {
    return (
      <Card className="bg-yellow-500/10 border-yellow-500/20">
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            <Droplet className="w-8 h-8 text-yellow-500" />
            <div>
              <h3 className="font-medium">Watermark Settings</h3>
              <p className="text-sm text-muted-foreground">Available on Pro and Studio plans</p>
            </div>
            <Button size="sm" className="ml-auto" onClick={() => window.location.href = '/pricing'}>
              Upgrade
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="watermark-settings-section">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Droplet className="w-5 h-5 text-primary" />
                <div>
                  <CardTitle className="text-xl font-serif">Watermark Settings</CardTitle>
                  <CardDescription>
                    Protect your photos with custom watermarks
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`px-2 py-1 rounded text-xs ${settings.enabled ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>
                  {settings.enabled ? "Enabled" : "Disabled"}
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="font-medium">Enable Watermarking</p>
                <p className="text-sm text-muted-foreground">Add watermarks to your gallery photos</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                data-testid="watermark-enabled-toggle"
              />
            </div>

            {settings.enabled && (
              <>
                {/* Watermark Type */}
                <div>
                  <Label className="mb-2 block">Watermark Type</Label>
                  <Tabs value={settings.type} onValueChange={(v) => setSettings(prev => ({ ...prev, type: v }))}>
                    <TabsList className="grid grid-cols-2 w-full max-w-xs">
                      <TabsTrigger value="text" className="flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        Text
                      </TabsTrigger>
                      <TabsTrigger value="image" className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        Image
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="space-y-4 mt-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Watermark Text</Label>
                          <Input
                            value={settings.text}
                            onChange={(e) => setSettings(prev => ({ ...prev, text: e.target.value }))}
                            placeholder="© Your Name"
                            data-testid="watermark-text-input"
                          />
                        </div>
                        <div>
                          <Label>Font Size: {settings.font_size}px</Label>
                          <Slider
                            value={[settings.font_size]}
                            onValueChange={([v]) => setSettings(prev => ({ ...prev, font_size: v }))}
                            min={12}
                            max={72}
                            step={2}
                            className="mt-2"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Text Color</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="color"
                            value={settings.color}
                            onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={settings.color}
                            onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                            className="w-28"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="image" className="mt-4">
                      {settings.watermark_image ? (
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <ImageIcon className="w-8 h-8 text-primary" />
                              <div>
                                <p className="font-medium">Custom watermark uploaded</p>
                                <p className="text-sm text-muted-foreground">{settings.watermark_image}</p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={deleteWatermarkImage}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                          <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-3">Upload a PNG with transparency for best results</p>
                          <label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                            <Button disabled={uploading} asChild size="sm">
                              <span>
                                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                Upload Image
                              </span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Position & Opacity */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Position</Label>
                    <Select
                      value={settings.position}
                      onValueChange={(v) => setSettings(prev => ({ ...prev, position: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="watermark-position-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WATERMARK_POSITIONS.map((pos) => (
                          <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Opacity: {settings.opacity}%</Label>
                    <Slider
                      value={[settings.opacity]}
                      onValueChange={([v]) => setSettings(prev => ({ ...prev, opacity: v }))}
                      min={10}
                      max={100}
                      step={5}
                      className="mt-3"
                    />
                  </div>
                </div>

                {/* Apply Settings */}
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Preview Images</p>
                      <p className="text-xs text-muted-foreground">Watermark shown in gallery view</p>
                    </div>
                    <Switch
                      checked={settings.apply_to_previews}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, apply_to_previews: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Downloaded Images</p>
                      <p className="text-xs text-muted-foreground">Watermark in downloads</p>
                    </div>
                    <Switch
                      checked={settings.apply_to_downloads}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, apply_to_downloads: checked }))}
                    />
                  </div>
                </div>

                {/* Preview */}
                {previewUrl && (
                  <div>
                    <Label className="mb-2 block">Preview</Label>
                    <img
                      src={previewUrl}
                      alt="Watermark preview"
                      className="max-w-full h-auto max-h-48 rounded-lg border border-border"
                      data-testid="watermark-preview-image"
                    />
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={loadPreview} disabled={previewLoading}>
                    {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
                    Preview
                  </Button>
                  <Button onClick={saveSettings} disabled={saving} data-testid="save-watermark-btn" size="sm">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Watermark Settings
                  </Button>
                </div>
              </>
            )}

            {!settings.enabled && (
              <p className="text-sm text-muted-foreground text-center py-2">
                Enable watermarking to protect your photos from unauthorized use.
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const avatarInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [fullProfile, setFullProfile] = useState(null);
  const [galleries, setGalleries] = useState([]);
  
  const [profile, setProfile] = useState({
    bio: "",
    tagline: "",
    social_links: {
      instagram: "",
      facebook: "",
      twitter: "",
      website: ""
    },
    event_types: [],
    request_types: [],
    company_name: "",
    portfolio_album_id: ""
  });
  
  const [userName, setUserName] = useState("");
  
  // Name edit dialog
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchGalleries();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/photographer/profile`, { withCredentials: true });
      setFullProfile(response.data);
      setProfile({
        bio: response.data.bio || "",
        tagline: response.data.tagline || "",
        social_links: response.data.social_links || {},
        event_types: response.data.event_types || [],
        request_types: response.data.request_types || [],
        company_name: response.data.company_name || "",
        portfolio_album_id: response.data.portfolio_album_id || ""
      });
      setUserName(response.data.user?.name || "");
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries`, { withCredentials: true });
      setGalleries(response.data);
    } catch (error) {
      console.error("Failed to fetch galleries");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/photographer/profile`, profile, { withCredentials: true });
      toast.success("Profile updated!");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/photographer/avatar`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Profile photo updated!");
      fetchProfile();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please use PNG, JPEG, SVG, or WebP");
      return;
    }
    
    // Validate size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2MB");
      return;
    }
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/photographer/logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo uploaded!");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/photographer/logo`, { withCredentials: true });
      toast.success("Logo removed");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    
    setSavingName(true);
    try {
      await axios.put(
        `${API}/photographer/profile/user`,
        { name: newName },
        { withCredentials: true }
      );
      toast.success("Name updated!");
      setUserName(newName);
      setNameDialogOpen(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error("Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleSetPortfolioAlbum = async (galleryId) => {
    try {
      await axios.put(
        `${API}/photographer/portfolio-album`,
        { gallery_id: galleryId || null },
        { withCredentials: true }
      );
      setProfile(prev => ({ ...prev, portfolio_album_id: galleryId }));
      toast.success(galleryId ? "Portfolio album set!" : "Portfolio album removed");
    } catch (error) {
      toast.error("Failed to update portfolio album");
    }
  };

  const copyBookingLink = () => {
    const link = `${window.location.origin}/book/${fullProfile?.photographer_id}`;
    navigator.clipboard.writeText(link);
    toast.success("Booking link copied!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const avatarUrl = fullProfile?.user?.picture?.startsWith("/api/") 
    ? `${API.replace("/api", "")}${fullProfile.user.picture}`
    : fullProfile?.user?.picture;

  const logoUrl = fullProfile?.logo_url?.startsWith("/api/")
    ? `${API.replace("/api", "")}${fullProfile.logo_url}`
    : fullProfile?.logo_url;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Profile Settings</h1>
        <p className="text-muted-foreground">Manage your profile, branding, and portfolio</p>
      </div>

      {/* User Info & Avatar */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif">Personal Information</CardTitle>
          <CardDescription>Your account details and profile photo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Avatar Upload */}
            <div className="relative group">
              <Avatar className="w-24 h-24">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback className="text-3xl">{userName?.[0]}</AvatarFallback>
              </Avatar>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
              />
              <button
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Camera className="w-6 h-6" />
                )}
              </button>
            </div>
            
            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Name</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      setNewName(userName);
                      setNameDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
                <p className="text-lg font-medium">{userName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="text-sm">{fullProfile?.user?.email}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Click on your photo to upload a new one (max 5MB)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Branding
          </CardTitle>
          <CardDescription>
            Customize your business name and logo. This replaces &quot;360Lumina&quot; throughout your experience.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-2 block">Company / Business Name</Label>
            <Input
              value={profile.company_name}
              onChange={(e) => setProfile(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="Leave empty to use '360Lumina'"
              data-testid="company-name-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This name will appear in headers and on your booking page
            </p>
          </div>
          
          <div>
            <Label className="mb-2 block">Logo</Label>
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <div className="relative group">
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="h-10 max-w-[160px] object-contain bg-muted/30 rounded p-1"
                  />
                  <button
                    className="absolute -top-2 -right-2 p-1 bg-destructive text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={handleDeleteLogo}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="h-10 w-32 bg-muted/30 rounded flex items-center justify-center text-muted-foreground text-xs">
                  No logo
                </div>
              )}
              
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {logoUrl ? "Change Logo" : "Upload Logo"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Recommended: 40px height, max 160px width. PNG, SVG, or WebP (max 2MB)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Watermark Settings - Moved from separate page */}
      <WatermarkSettings />

      {/* Portfolio Album */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-primary" />
            Portfolio Album
          </CardTitle>
          <CardDescription>
            Select a gallery to showcase on your booking page. The cover photo will be your primary image.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select 
            value={profile.portfolio_album_id || "none"}
            onValueChange={(value) => handleSetPortfolioAlbum(value === "none" ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a portfolio album" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— No portfolio album —</SelectItem>
              {galleries.map((gallery) => (
                <SelectItem key={gallery.gallery_id} value={gallery.gallery_id}>
                  {gallery.title} ({gallery.asset_count || 0} photos)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {galleries.length === 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Create a gallery first to set it as your portfolio
            </p>
          )}
        </CardContent>
      </Card>

      {/* Booking Link */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-primary" />
            Your Booking Link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={`${window.location.origin}/book/${fullProfile?.photographer_id || ""}`}
              readOnly
              className="bg-muted/50"
            />
            <Button variant="outline" onClick={copyBookingLink} data-testid="copy-booking-link">
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Share this link with customers so they can book your services
          </p>
        </CardContent>
      </Card>

      {/* Profile Details */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif">Public Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-2 block">Tagline</Label>
            <Input
              value={profile.tagline}
              onChange={(e) => setProfile(prev => ({ ...prev, tagline: e.target.value }))}
              placeholder="e.g., Capturing Life's Beautiful Moments"
              data-testid="tagline-input"
            />
          </div>
          
          <div>
            <Label className="mb-2 block">Bio</Label>
            <Textarea
              value={profile.bio}
              onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell customers about yourself and your photography style..."
              rows={5}
              data-testid="bio-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Social Links */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif">Social Links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">Instagram</Label>
            <Input
              value={profile.social_links.instagram || ""}
              onChange={(e) => setProfile(prev => ({
                ...prev,
                social_links: { ...prev.social_links, instagram: e.target.value }
              }))}
              placeholder="https://instagram.com/yourhandle"
              data-testid="instagram-input"
            />
          </div>
          
          <div>
            <Label className="mb-2 block">Facebook</Label>
            <Input
              value={profile.social_links.facebook || ""}
              onChange={(e) => setProfile(prev => ({
                ...prev,
                social_links: { ...prev.social_links, facebook: e.target.value }
              }))}
              placeholder="https://facebook.com/yourpage"
              data-testid="facebook-input"
            />
          </div>
          
          <div>
            <Label className="mb-2 block">Twitter / X</Label>
            <Input
              value={profile.social_links.twitter || ""}
              onChange={(e) => setProfile(prev => ({
                ...prev,
                social_links: { ...prev.social_links, twitter: e.target.value }
              }))}
              placeholder="https://twitter.com/yourhandle"
              data-testid="twitter-input"
            />
          </div>
          
          <div>
            <Label className="mb-2 block">Website</Label>
            <Input
              value={profile.social_links.website || ""}
              onChange={(e) => setProfile(prev => ({
                ...prev,
                social_links: { ...prev.social_links, website: e.target.value }
              }))}
              placeholder="https://yourwebsite.com"
              data-testid="website-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Services */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            Services Offered
          </CardTitle>
          <CardDescription>
            Define the event types and services you offer. These will appear on your booking page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Event Types */}
          <div>
            <Label className="mb-2 block">Event Types</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Types of events you photograph (e.g., Weddings, Portraits, Sports)
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {profile.event_types.map((type, index) => (
                <Badge 
                  key={index} 
                  variant="secondary" 
                  className="px-3 py-1.5 text-sm cursor-pointer hover:bg-destructive/20 group"
                  onClick={() => setProfile(prev => ({
                    ...prev,
                    event_types: prev.event_types.filter((_, i) => i !== index)
                  }))}
                >
                  {type}
                  <X className="w-3 h-3 ml-1.5 group-hover:text-destructive" />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add event type..."
                className="max-w-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const newType = e.target.value.trim();
                    if (!profile.event_types.includes(newType)) {
                      setProfile(prev => ({
                        ...prev,
                        event_types: [...prev.event_types, newType]
                      }));
                    }
                    e.target.value = '';
                  }
                }}
                data-testid="event-types-input"
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => {
                  const input = e.target.closest('.flex').querySelector('input');
                  if (input.value.trim()) {
                    const newType = input.value.trim();
                    if (!profile.event_types.includes(newType)) {
                      setProfile(prev => ({
                        ...prev,
                        event_types: [...prev.event_types, newType]
                      }));
                    }
                    input.value = '';
                  }
                }}
              >
                Add
              </Button>
            </div>
            {profile.event_types.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Suggestions: Wedding, Portrait, Event, Sports, Headshot, Family, Newborn, Engagement
              </p>
            )}
          </div>
          
          {/* Request Types / Services */}
          <div className="border-t pt-6">
            <Label className="mb-2 block">Service Types</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Types of deliverables you offer (e.g., Photos, Video, Drone)
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {profile.request_types.map((type, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="px-3 py-1.5 text-sm cursor-pointer hover:bg-destructive/20 group"
                  onClick={() => setProfile(prev => ({
                    ...prev,
                    request_types: prev.request_types.filter((_, i) => i !== index)
                  }))}
                >
                  {type}
                  <X className="w-3 h-3 ml-1.5 group-hover:text-destructive" />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add service type..."
                className="max-w-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const newType = e.target.value.trim();
                    if (!profile.request_types.includes(newType)) {
                      setProfile(prev => ({
                        ...prev,
                        request_types: [...prev.request_types, newType]
                      }));
                    }
                    e.target.value = '';
                  }
                }}
                data-testid="request-types-input"
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => {
                  const input = e.target.closest('.flex').querySelector('input');
                  if (input.value.trim()) {
                    const newType = input.value.trim();
                    if (!profile.request_types.includes(newType)) {
                      setProfile(prev => ({
                        ...prev,
                        request_types: [...prev.request_types, newType]
                      }));
                    }
                    input.value = '';
                  }
                }}
              >
                Add
              </Button>
            </div>
            {profile.request_types.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Suggestions: Photography, Videography, Drone, Photo + Video, Editing, Prints
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pb-8">
        <Button 
          className="btn-primary rounded-sm"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-profile-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Name Edit Dialog */}
      <Dialog open={nameDialogOpen} onOpenChange={setNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Name</DialogTitle>
            <DialogDescription>
              Update your display name
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label className="mb-2 block">Name</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Your name"
              data-testid="edit-name-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNameDialogOpen(false)} disabled={savingName}>
              Cancel
            </Button>
            <Button onClick={handleSaveName} disabled={savingName}>
              {savingName ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
